'use client'

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { format } from 'date-fns'
import { useQuery } from 'convex/react'
import {
  CalendarCheck,
  CheckCircle,
  ClipboardList,
  Loader2,
  Phone,
  UserCheck,
} from 'lucide-react'

import { api } from '@/convex/_generated/api'
import type { Doc } from '@/convex/_generated/dataModel'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import type {
  BookingData,
  BookingDurationMinutes,
  BookingSetter,
  FlowType,
} from '@/types/booking'
import { useWhatsApp as flagUseWhatsApp } from '@/config/flags'
import {
  clearBookingDraftMarker,
  clearUnsafeBookingDraftStorage,
  writeBookingDraftMarker,
} from '@/lib/booking-draft-marker'
import {
  createBookingSubmitter,
  type BookingSubmitter,
} from '@/lib/booking-submission'

import StepPhoneVerify from '@/components/booking/step-phone-verify'
import StepPatientSelect from '@/components/booking/step-patient-select'
import StepNewPatientDetails from '@/components/booking/step-new-patient-details'
import StepChooseFlow from '@/components/booking/step-choose-flow'
import StepDoctorChoice from '@/components/booking/step-doctor-choice'
import StepSelectedDoctorBanner from '@/components/booking/step-selected-doctor-banner'
import StepDateTime from '@/components/booking/step-date-time'
import StepAppointmentSummary from '@/components/booking/step-appointment-summary'
import {
  applyDoctorToBookingData,
  resolvePreferredDoctor,
} from '@/components/booking/booking-doctors'

type PatientDoc = Doc<'patients'>

type BookingWizardStep =
  | 'phone'
  | 'patient-loading'
  | 'patient-select'
  | 'new-patient-details'
  | 'flow-choice'
  | 'doctor-choice'
  | 'slot-choice'
  | 'summary'

interface BookingWizardModalProps {
  isOpen: boolean
  onClose: () => void
  bookingData: BookingData
  setBookingData: BookingSetter
  createInitialBookingData: () => BookingData
  initialStep?: BookingWizardStep
  allowedFlowTypes?: FlowType[]
}

/* ────────────────────────────────────────────── */
/*  Safe progress detection                        */
/* ────────────────────────────────────────────── */

function hasMeaningfulBookingProgress(
  data: BookingData,
  step: BookingWizardStep,
  initialStep: BookingWizardStep,
) {
  return Boolean(
    step !== initialStep ||
      data.phone ||
      data.name ||
      data.dob ||
      data.gender ||
      data.email ||
      data.phoneVerified ||
      data.patientMode ||
      data.existingPatientId ||
      data.existingPatientHumanId ||
      data.flowType ||
      data.doctorID ||
      data.doctorName ||
      data.bookingDate ||
      data.slot ||
      data.durationMinutes,
  )
}

/* ────────────────────────────────────────────── */
/*  Booking helpers                               */
/* ────────────────────────────────────────────── */

const VALID_DURATIONS: readonly BookingDurationMinutes[] = [15, 30, 60]

async function postBooking(payload: Record<string, any>) {
  const response = await fetch('/api/bookings', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })

  if (!response.ok) {
    let message = 'Failed to create booking'

    try {
      const body = await response.json()

      if (body?.error) {
        message = typeof body.error === 'string' ? body.error : 'Booking error'
      }
    } catch {
      // Keep fallback message.
    }

    throw new Error(message)
  }

  return (await response.json()) as { ok: true; id: string }
}

function normalizeBookingDuration(
  value: number | undefined,
  fallback: BookingDurationMinutes,
): BookingDurationMinutes {
  return VALID_DURATIONS.includes(value as BookingDurationMinutes)
    ? (value as BookingDurationMinutes)
    : fallback
}

function diffMinutes(start: string, end: string) {
  const toMinutes = (time: string) => {
    const [hours, minutes] = time.split(':').map(Number)
    return hours * 60 + minutes
  }

  if (!start || !end) return 0
  if (!/^\d{2}:\d{2}$/.test(start)) return 0
  if (!/^\d{2}:\d{2}$/.test(end)) return 0

  return Math.max(0, toMinutes(end) - toMinutes(start))
}

function deriveSlotPayload(slot: string) {
  const match = slot.match(/^(\d{2})_(\d{2})_(\d{2})_(\d{2})$/)

  if (!match) {
    const normalized = slot?.toLowerCase()
    const slotBucket = normalized === 'afternoon' ? 'afternoon' : 'morning'

    if (slotBucket === 'afternoon') {
      return { slotBucket, timeStart: '14:00', timeEnd: '17:00' }
    }

    return { slotBucket, timeStart: '09:30', timeEnd: '13:30' }
  }

  const [, sh, sm, eh, em] = match
  const startRaw = Number(sh) * 60 + Number(sm)
  const endRaw = Number(eh) * 60 + Number(em)
  const [start, end] =
    endRaw >= startRaw ? [startRaw, endRaw] : [endRaw, startRaw]

  const formatMinutes = (minutes: number) =>
    `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(
      minutes % 60,
    ).padStart(2, '0')}`

  return {
    slotBucket: start < 13 * 60 + 30 ? 'morning' : 'afternoon',
    timeStart: formatMinutes(start),
    timeEnd: formatMinutes(end),
  }
}

function normalizeExistingGender(value: string | null | undefined): string {
  const normalized = (value ?? '').trim().toLowerCase()

  if (normalized === 'male') return 'male'
  if (normalized === 'female') return 'female'
  if (normalized === 'other') return 'other'
  if (normalized === 'prefer-not-to-say') return 'prefer-not-to-say'

  return ''
}

function parseDobStringToDate(
  dob: string | null | undefined,
): Date | undefined {
  if (!dob) return undefined

  const parts = dob.split('-')

  if (parts.length === 3) {
    const [year, month, day] = parts.map(Number)
    const date = new Date(year, month - 1, day)

    if (!Number.isNaN(date.getTime())) return date
  }

  const parsed = new Date(dob)

  if (!Number.isNaN(parsed.getTime())) return parsed

  return undefined
}

const STEP_META: Record<
  BookingWizardStep,
  {
    title: string
    progressIndex: number
    icon: React.ComponentType<{ className?: string }>
  }
> = {
  phone: {
    title: 'Verify Phone Number',
    progressIndex: 0,
    icon: Phone,
  },
  'patient-loading': {
    title: 'Finding Your ReACH Profile',
    progressIndex: 1,
    icon: UserCheck,
  },
  'patient-select': {
    title: 'Verify Patient',
    progressIndex: 1,
    icon: UserCheck,
  },
  'new-patient-details': {
    title: 'Patient Details',
    progressIndex: 1,
    icon: UserCheck,
  },
  'flow-choice': {
    title: 'Choose Care Type',
    progressIndex: 2,
    icon: ClipboardList,
  },
  'doctor-choice': {
    title: 'Choose Clinician',
    progressIndex: 3,
    icon: UserCheck,
  },
  'slot-choice': {
    title: 'Select Date & Time',
    progressIndex: 4,
    icon: CalendarCheck,
  },
  summary: {
    title: 'Appointment Summary',
    progressIndex: 5,
    icon: CheckCircle,
  },
}

const PROGRESS_STEPS = [
  { label: 'Phone', icon: Phone },
  { label: 'Patient', icon: UserCheck },
  { label: 'Care', icon: ClipboardList },
  { label: 'Doctor', icon: UserCheck },
  { label: 'Slot', icon: CalendarCheck },
  { label: 'Confirm', icon: CheckCircle },
] as const

const BookingWizardModal: React.FC<BookingWizardModalProps> = ({
  isOpen,
  onClose,
  bookingData,
  setBookingData,
  createInitialBookingData,
  initialStep = 'phone',
  allowedFlowTypes,
}) => {
  const [step, setStep] = useState<BookingWizardStep>(initialStep)
  const [verifiedPhone, setVerifiedPhone] = useState<string | null>(null)
  const [selectedPatientId, setSelectedPatientId] = useState<string | null>(
    null,
  )
  const [isBooking, setIsBooking] = useState(false)
  const [isBooked, setIsBooked] = useState(false)

  const contentRef = useRef<HTMLDivElement | null>(null)
  const wasOpenRef = useRef(false)
  const whatsappEnabled = flagUseWhatsApp

  // Owns the synchronous double-submit guard and the generate_lead
  // conversion (see src/lib/booking-submission.ts). Created once per mount.
  const bookingSubmitterRef = useRef<BookingSubmitter<
    Record<string, any>
  > | null>(null)
  if (!bookingSubmitterRef.current) {
    bookingSubmitterRef.current = createBookingSubmitter({
      post: postBooking,
      onSuccess: () => {
        clearBookingDraftMarker()
        clearUnsafeBookingDraftStorage()
        setIsBooked(true)
      },
      onError: (err) => {
        const message =
          err instanceof Error ? err.message : 'Could not save booking.'

        if (
          message.toLowerCase().includes('time slot') ||
          message.toLowerCase().includes('taken')
        ) {
          alert('That time slot was just taken. Please pick another one.')
        } else {
          alert(message)
        }
      },
    })
  }

  const patientMatches = useQuery(
    api.patient.getByPhone,
    verifiedPhone ? { phone: verifiedPhone } : 'skip',
  ) as PatientDoc[] | undefined

  const currentStepMeta = STEP_META[step]
  const currentProgressIndex = currentStepMeta.progressIndex

  const mobileProgressSteps = useMemo(() => {
    const total = PROGRESS_STEPS.length
    const current = currentProgressIndex

    let start = Math.max(0, current - 1)
    let end = Math.min(total - 1, current + 1)

    if (current === 0) {
      start = 0
      end = Math.min(total - 1, 2)
    }

    if (current === total - 1) {
      start = Math.max(0, total - 3)
      end = total - 1
    }

    return PROGRESS_STEPS.map((item, index) => ({
      ...item,
      index,
    })).slice(start, end + 1)
  }, [currentProgressIndex])

  useEffect(() => {
    clearUnsafeBookingDraftStorage()
  }, [])

  useEffect(() => {
    if (!isOpen) {
      wasOpenRef.current = false
      return
    }

    if (wasOpenRef.current) return

    wasOpenRef.current = true
    clearUnsafeBookingDraftStorage()
    setIsBooking(false)
    setIsBooked(false)
    // A fresh open is a new booking intent — unlock the one-shot submit
    // guard (an in-flight request keeps its own lock until it settles).
    bookingSubmitterRef.current?.reset()

    const hasProgress = hasMeaningfulBookingProgress(
      bookingData,
      step,
      initialStep,
    )

    if (!hasProgress) {
      setStep(initialStep)
      setVerifiedPhone(null)
      setSelectedPatientId(null)
    }
  }, [bookingData, initialStep, isOpen, step])

  useEffect(() => {
    if (!isOpen) return
    if (isBooking || isBooked) return

    const hasProgress = hasMeaningfulBookingProgress(
      bookingData,
      step,
      initialStep,
    )

    if (!hasProgress) return

    writeBookingDraftMarker()
  }, [bookingData, initialStep, isBooked, isBooking, isOpen, step])

  const resetWizardState = useCallback(() => {
    setStep(initialStep)
    setVerifiedPhone(null)
    setSelectedPatientId(null)
    setIsBooking(false)
    setIsBooked(false)
    setBookingData(createInitialBookingData())
    bookingSubmitterRef.current?.reset()
  }, [createInitialBookingData, initialStep, setBookingData])

  const handleSoftClose = useCallback(() => {
    onClose()
  }, [onClose])

  const handleCompleteClose = useCallback(() => {
    clearBookingDraftMarker()
    clearUnsafeBookingDraftStorage()
    resetWizardState()
    onClose()
  }, [onClose, resetWizardState])

  const hydrateFromPatient = useCallback(
    (patient: PatientDoc) => {
      const patientId = String(patient._id)
      const preferredPsychologist =
        ((patient as any).preferred_psychologist ?? '').trim() || null

      setSelectedPatientId(patientId)

      setBookingData((prev) => ({
        ...prev,
        name: ((patient as any).name ?? '').trim(),
        dob: parseDobStringToDate((patient as any).dob ?? null),
        gender: normalizeExistingGender((patient as any).gender),
        email: ((patient as any).email ?? '').trim(),
        phone: verifiedPhone ?? prev.phone,
        existingPatientId: patientId,
        existingPatientHumanId: (patient as any).patient_id ?? null,
        preferredPsychologist,
        patientMode: 'existing',
        phoneVerified: true,
        flowType: null,
        assessmentType: null,
        doctorID: '',
        doctorName: '',
        doctorSpecialty: '',
        bookingDate: undefined,
        slot: '',
        durationMinutes: undefined,
      }))
    },
    [setBookingData, verifiedPhone],
  )

  useEffect(() => {
    if (step !== 'patient-loading') return
    if (patientMatches === undefined) return

    if (patientMatches.length === 0) {
      setBookingData((prev) => ({
        ...prev,
        patientMode: 'new',
        existingPatientId: null,
        existingPatientHumanId: null,
        preferredPsychologist: null,
        phoneVerified: true,
      }))
      setStep('new-patient-details')
      return
    }

    if (patientMatches.length === 1) {
      hydrateFromPatient(patientMatches[0])
      setStep('flow-choice')
      return
    }

    setStep('patient-select')
  }, [hydrateFromPatient, patientMatches, setBookingData, step])

  const handlePhoneVerified = (phone: string) => {
    setVerifiedPhone(phone)
    setStep('patient-loading')
  }

  const handleAddNewPatient = () => {
    setSelectedPatientId(null)

    setBookingData((prev) => ({
      ...prev,
      name: '',
      dob: undefined,
      gender: '',
      email: '',
      existingPatientId: null,
      existingPatientHumanId: null,
      preferredPsychologist: null,
      patientMode: 'new',
      phoneVerified: true,
      flowType: null,
      assessmentType: null,
      doctorID: '',
      doctorName: '',
      doctorSpecialty: '',
      bookingDate: undefined,
      slot: '',
      durationMinutes: undefined,
    }))

    setStep('new-patient-details')
  }

  const handleSelectFlow = (flow: FlowType) => {
    const preferredDoctor = resolvePreferredDoctor(
      bookingData.preferredPsychologist,
    )

    setBookingData((prev) => {
      const base: BookingData = {
        ...prev,
        flowType: flow,
        assessmentType: null,
        bookingDate: undefined,
        slot: '',
        durationMinutes: undefined,
      }

      if (!preferredDoctor) {
        return {
          ...base,
          doctorID: '',
          doctorName: '',
          doctorSpecialty: '',
        }
      }

      return applyDoctorToBookingData(base, preferredDoctor)
    })

    setStep(preferredDoctor ? 'slot-choice' : 'doctor-choice')
  }

  const handleBack = () => {
    if (step === 'patient-select') {
      setStep('phone')
      return
    }

    if (step === 'new-patient-details') {
      if (patientMatches && patientMatches.length > 0) {
        setStep('patient-select')
        return
      }

      setStep('phone')
      return
    }

    if (step === 'flow-choice') {
      if (bookingData.patientMode === 'new') {
        setStep('new-patient-details')
        return
      }

      if (patientMatches && patientMatches.length > 1) {
        setStep('patient-select')
        return
      }

      setStep('phone')
      return
    }

    if (step === 'doctor-choice') {
      setStep('flow-choice')
      return
    }

    if (step === 'slot-choice') {
      setStep('doctor-choice')
      return
    }

    if (step === 'summary') {
      setStep('slot-choice')
    }
  }

  const handleBook = async () => {
    const data = bookingData

    if (!data.flowType || data.flowType === 'Assessment') return
    if (!(data.name && data.dob && data.gender && data.phone)) return
    if (!(data.doctorID && data.doctorName && data.bookingDate && data.slot)) {
      return
    }

    const { slotBucket, timeStart, timeEnd } = deriveSlotPayload(data.slot)

    const computedDuration = diffMinutes(timeStart, timeEnd)

    const durationMinutes = normalizeBookingDuration(
      computedDuration || data.durationMinutes,
      data.doctorID === '1' ? 15 : 60,
    )

    const payload = {
      name: data.name.trim(),
      dob: format(data.dob, 'yyyy-MM-dd'),
      gender: data.gender,
      phone: data.phone,
      email: data.email.trim(),
      doctorID: data.doctorID,
      doctorName: data.doctorName,
      doctorSpecialty: data.doctorSpecialty || undefined,
      bookingDate: format(data.bookingDate, 'yyyy-MM-dd'),
      slot: slotBucket,
      timeStart,
      timeEnd,
      status: 'Booking confirmed',
      flowType: data.flowType,
      durationMinutes,
      sendWhatsApp: whatsappEnabled,
      existingPatientId: data.existingPatientId ?? undefined,
      existingPatientHumanId: data.existingPatientHumanId ?? undefined,
      patient_id: data.existingPatientHumanId ?? undefined,
    }

    const submitter = bookingSubmitterRef.current
    if (!submitter) return

    setIsBooking(true)

    try {
      // Success/failure handling (confirmation state, alerts) and the
      // generate_lead conversion live in the submitter; its synchronous
      // guard makes a second overlapping call a no-op.
      await submitter.submit(payload)
    } finally {
      setIsBooking(false)
    }
  }

  const headerTitle = useMemo(() => {
    if (isBooked) return 'Booking Confirmed'
    if (isBooking) return 'Processing Booking'
    return currentStepMeta.title
  }, [currentStepMeta.title, isBooked, isBooking])

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (open) return
        if (isBooking) return

        if (isBooked) {
          handleCompleteClose()
          return
        }

        handleSoftClose()
      }}
    >
      <DialogContent
        ref={contentRef}
        data-scroll-container
        className="z-50 max-h-[90vh] w-[95vw] max-w-6xl overflow-y-auto overflow-x-hidden bg-gradient-to-br from-blue-50 via-white to-purple-50 p-4 sm:max-h-[95vh] sm:p-6"
      >
        <DialogHeader className="pb-4 text-center sm:pb-7">
          <DialogTitle className="mx-auto mb-3 w-full bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-center text-xl font-bold text-transparent sm:mb-5 sm:text-2xl md:text-3xl">
            {headerTitle}
          </DialogTitle>

          {!isBooking && !isBooked && (
            <>
              <div className="flex items-center justify-center overflow-hidden px-3 py-3 sm:hidden">
                <div className="flex items-center justify-center gap-1.5">
                  {mobileProgressSteps.map((item, visibleIndex) => {
                    const Icon = item.icon
                    const index = item.index
                    const active = index === currentProgressIndex
                    const done = index < currentProgressIndex

                    return (
                      <div key={item.label} className="flex items-center">
                        <div className="flex flex-col items-center">
                          <div className="relative flex h-12 w-12 items-center justify-center">
                            {active && (
                              <>
                                <span className="absolute inset-0 rounded-full bg-blue-500/20 blur-md" />
                                <span className="absolute inset-1 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 opacity-30 animate-ping" />
                              </>
                            )}

                            <div
                              className={`relative z-10 flex h-9 w-9 items-center justify-center rounded-full border transition-all duration-300 ${
                                active
                                  ? 'scale-110 border-blue-300 bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-[0_0_0_6px_rgba(59,130,246,0.14),0_10px_24px_rgba(37,99,235,0.35)]'
                                  : done
                                    ? 'border-emerald-300 bg-gradient-to-r from-green-400 to-emerald-500 text-white shadow-sm'
                                    : 'border-gray-300 bg-gray-200 text-gray-500'
                              }`}
                            >
                              {done ? (
                                <CheckCircle className="h-4 w-4" />
                              ) : (
                                <Icon className="h-4 w-4" />
                              )}
                            </div>
                          </div>

                          <p
                            className={`mt-0.5 text-[10px] font-medium ${
                              active
                                ? 'text-blue-600'
                                : done
                                  ? 'text-green-600'
                                  : 'text-gray-500'
                            }`}
                          >
                            {item.label}
                          </p>
                        </div>

                        {visibleIndex < mobileProgressSteps.length - 1 && (
                          <div
                            className={`mx-1 h-0.5 w-7 rounded-full ${
                              done
                                ? 'bg-gradient-to-r from-green-400 to-emerald-500'
                                : 'bg-gray-200'
                            }`}
                          />
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>

              <div className="hidden items-center justify-center overflow-x-auto px-4 py-4 sm:flex">
                <div className="flex min-w-max items-center gap-3">
                  {PROGRESS_STEPS.map((item, index) => {
                    const Icon = item.icon
                    const active = index === currentProgressIndex
                    const done = index < currentProgressIndex

                    return (
                      <div key={item.label} className="flex items-center">
                        <div className="flex flex-col items-center">
                          <div className="relative flex h-14 w-14 items-center justify-center">
                            {active && (
                              <>
                                <span className="absolute inset-0 rounded-full bg-blue-500/20 blur-md" />
                                <span className="absolute inset-1 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 opacity-30 animate-ping" />
                              </>
                            )}

                            <div
                              className={`relative z-10 flex h-11 w-11 items-center justify-center rounded-full border transition-all duration-300 ${
                                active
                                  ? 'scale-110 border-blue-300 bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-[0_0_0_6px_rgba(59,130,246,0.14),0_10px_24px_rgba(37,99,235,0.35)]'
                                  : done
                                    ? 'border-emerald-300 bg-gradient-to-r from-green-400 to-emerald-500 text-white shadow-sm'
                                    : 'border-gray-300 bg-gray-200 text-gray-500'
                              }`}
                            >
                              {done ? (
                                <CheckCircle className="h-5 w-5" />
                              ) : (
                                <Icon className="h-5 w-5" />
                              )}
                            </div>
                          </div>

                          <p
                            className={`mt-0.5 text-xs font-medium ${
                              active
                                ? 'text-blue-600'
                                : done
                                  ? 'text-green-600'
                                  : 'text-gray-500'
                            }`}
                          >
                            {item.label}
                          </p>
                        </div>

                        {index < PROGRESS_STEPS.length - 1 && (
                          <div
                            className={`mx-2 h-0.5 w-8 rounded-full ${
                              done
                                ? 'bg-gradient-to-r from-green-400 to-emerald-500'
                                : 'bg-gray-200'
                            }`}
                          />
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>
            </>
          )}

          {bookingData.flowType && !isBooking && !isBooked && (
            <div className="mt-4 flex justify-center">
              <div className="inline-flex items-center gap-2 rounded-full bg-white/80 px-4 py-2 text-xs font-medium text-gray-700 shadow-sm ring-1 ring-gray-200 sm:text-sm">
                <span className="h-2 w-2 rounded-full bg-blue-600" />
                {bookingData.flowType}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-xs"
                  onClick={() => setStep('flow-choice')}
                >
                  Change
                </Button>
              </div>
            </div>
          )}
        </DialogHeader>

        {isBooking && (
          <div className="flex flex-col items-center justify-center py-12 text-center sm:py-16">
            <Loader2 className="h-14 w-14 animate-spin text-blue-600 sm:h-20 sm:w-20" />
            <h3 className="mt-5 text-lg font-bold text-gray-900 sm:text-2xl">
              Processing your booking…
            </h3>
            <p className="mt-1 text-sm text-gray-600 sm:text-base">
              Please wait while we confirm your appointment.
            </p>
          </div>
        )}

        {isBooked && !isBooking && (
          <div className="py-6 text-center sm:py-10">
            <span className="mx-auto mb-5 flex h-20 w-20 items-center justify-center rounded-full bg-green-100 sm:h-24 sm:w-24">
              <CheckCircle className="h-12 w-12 text-green-600 sm:h-16 sm:w-16" />
            </span>

            <h3 className="px-2 text-xl font-bold text-green-800 sm:text-2xl md:text-3xl">
              Your booking has been confirmed.
            </h3>

            <p className="mx-auto mt-2 max-w-xl text-sm text-gray-600 sm:text-base">
              We have received your appointment request. One of our
              representatives will reach out if any follow-up is needed.
            </p>

            <Button
              type="button"
              onClick={handleCompleteClose}
              size="lg"
              className="mt-6 rounded-full bg-gradient-to-r from-blue-600 to-purple-600 px-8 text-white shadow-lg"
            >
              Close
            </Button>

            <div className="mx-auto mt-4 max-w-md rounded-md border border-blue-200 bg-blue-50 px-4 py-3">
              <p className="text-xs text-blue-800 sm:text-sm">
                Questions? Reach us between{' '}
                <span className="font-semibold">10&nbsp;AM – 5&nbsp;PM</span>.
              </p>
            </div>
          </div>
        )}

        {!isBooking && !isBooked && (
          <>
            {step === 'phone' && (
              <StepPhoneVerify
                useWhatsApp={whatsappEnabled}
                bookingData={bookingData}
                setBookingData={setBookingData}
                onVerified={handlePhoneVerified}
              />
            )}

            {step === 'patient-loading' && (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <Loader2 className="h-10 w-10 animate-spin text-blue-600" />
                <h3 className="mt-4 text-lg font-semibold text-gray-900">
                  Looking up your ReACH profile…
                </h3>
                <p className="mt-1 text-sm text-gray-600">
                  This helps us avoid asking for details we already have.
                </p>
              </div>
            )}

            {step === 'patient-select' && patientMatches && (
              <StepPatientSelect
                matches={patientMatches}
                selectedPatientId={selectedPatientId}
                onSelectPatient={hydrateFromPatient}
                onAddNewPatient={handleAddNewPatient}
                onPrevious={handleBack}
                onNext={() => setStep('flow-choice')}
              />
            )}

            {step === 'new-patient-details' && (
              <StepNewPatientDetails
                bookingData={bookingData}
                setBookingData={setBookingData}
                existingPatientsForPhone={patientMatches ?? []}
                onUseExistingPatient={(patient) => {
                  hydrateFromPatient(patient)
                  setStep('flow-choice')
                }}
                onPrevious={handleBack}
                onNext={() => setStep('flow-choice')}
              />
            )}

            {step === 'flow-choice' && (
              <StepChooseFlow
                selectedFlow={bookingData.flowType}
                onSelect={handleSelectFlow}
                allowedFlows={allowedFlowTypes}
              />
            )}

            {step === 'doctor-choice' && (
              <StepDoctorChoice
                bookingData={bookingData}
                setBookingData={setBookingData}
                onPrevious={handleBack}
                onNext={() => setStep('slot-choice')}
              />
            )}

            {step === 'slot-choice' && (
              <div className="flex flex-col">
                <StepSelectedDoctorBanner
                  bookingData={bookingData}
                  onChangeDoctor={() => setStep('doctor-choice')}
                />

                <StepDateTime
                  mode="appointment"
                  bookingData={bookingData}
                  setBookingData={setBookingData}
                  selectedDoctorName={bookingData.doctorName}
                  onPrevious={handleBack}
                  onNext={() => setStep('summary')}
                />
              </div>
            )}

            {step === 'summary' && (
              <StepAppointmentSummary
                bookingData={bookingData}
                isBooking={isBooking}
                onPrevious={handleBack}
                onBook={handleBook}
              />
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default BookingWizardModal
